/**
 * Who may reach each settings destination (audit PRT-6).
 *
 * PRT-6 was reported against `/settings/parts`, but the defect was a class:
 * **no `/settings/*` route carried a permission guard at all.** Only the API
 * held the line, so anyone typing a settings URL reached the page and got an
 * error-shaped screen rather than a clean denial — leaking the existence and
 * shape of admin configuration along the way.
 *
 * This table is the SINGLE declaration. The hub filters its cards from it and
 * the router guards its routes from it, so a page cannot be visible-but-
 * unreachable or hidden-but-reachable — the two states you get when the two
 * lists are maintained separately, which is exactly what happened here.
 *
 * Deliberately free of React and icon imports: `App.tsx` imports it eagerly to
 * build the routes, and the hub page is lazily loaded. Pulling this out of the
 * page module is what keeps the hub's icon set out of the initial bundle.
 *
 * `permission` defaults to `settings:read` when omitted — the floor for the
 * settings area as a whole, matching the nav entry for `/settings`. It is not
 * "no guard": that was the bug.
 */
export interface SettingsAccess {
  /** Defaults to `settings:read`. */
  permission?: string
  ownerOnly?: boolean
  /** D22: the destination also needs this module switched on. */
  moduleKey?: string
}

export const SETTINGS_FLOOR_PERMISSION = 'settings:read'

/**
 * FF-AUDIT-080: areas that are Settings-hub cards rather than sidebar entries
 * (M-02) and so have no nav registration for the page-access matrix to read.
 * The permission is the one the page itself needs — the same the hub card and
 * the feature's own nav definition carry.
 */
export const HUB_AREA_PERMISSIONS: Record<string, string> = {
  '/integrations': 'integrations:read',
  '/automation': 'settings:read',
}

export const SETTINGS_ACCESS: Record<string, SettingsAccess> = {
  '/settings': {},
  // FF-AUDIT-20260914-210459-095: a Supervisor (settings:read) landed on
  // pages that LOOK editable — disabled inputs, no Save, no sentence saying
  // why — and on a Users register listing every e-mail and 2FA state. The
  // purely administrative pages are reachable only by those who can change
  // them; the Users register by those who can manage users (users:read stays
  // the pickers' permission). Notifications and Exports stay readable: a
  // supervisor's alert rules and export jobs are theirs to see.
  '/settings/company': { permission: 'settings:update' },
  '/settings/policies': { permission: 'settings:update' },
  '/settings/numbering': { permission: 'settings:update' },
  '/settings/modules': { permission: 'settings:update' },
  '/settings/notifications': {},
  '/settings/governance': { permission: 'settings:update' },
  '/settings/users': { permission: 'users:update' },
  '/settings/facilities': { permission: 'facilities:manage' },
  '/settings/features': { permission: 'settings:update' },
  '/settings/access': { permission: 'settings:update' },
  '/settings/assignment-rules': { permission: 'settings:update' },
  '/settings/work-transfer': { permission: 'work:transfer' },
  '/settings/teams': { permission: 'workforce:read' },
  '/settings/localization': { permission: 'settings:update' },
  '/settings/search': { permission: 'settings:update' },
  '/settings/workforce': { permission: 'settings:update' },
  '/settings/checks': { permission: 'checks:manage' },
  '/settings/audit-log': { permission: 'audit:read' },
  /**
   * 08-12 audit D4 item 3. The FLOOR permission on purpose: the register lists
   * only the caller's OWN export jobs (`listExports` filters on
   * `requestedBy`), so anyone who can reach Settings can see their own
   * downloads. Gating it on `reports:read` would hide a user's own files from
   * them whenever an export was queued from a page they can no longer open.
   */
  '/settings/parts': { permission: 'settings:update', moduleKey: 'parts' },
  // F24A: the metric-definition catalogue — same floor as managing an asset's
  // own bindings (`equipment:update`), not `settings:update`, so the people
  // who already maintain equipment records can maintain what a reading means.
  '/settings/equipment-metrics': { permission: 'equipment:update' },
  '/settings/change-control': { permission: 'settings:update', moduleKey: 'change_control' },
  '/settings/hr-records': { permission: 'hr_records:manage', moduleKey: 'hr_records' },
  '/settings/hr-access-log': { permission: 'hr_records:manage', moduleKey: 'hr_records' },
  '/settings/deleted-forms': { ownerOnly: true },
  '/settings/system-health': { ownerOnly: true },
}

/** The access rule for a path, or undefined when the path is not declared. */
export function settingsAccessFor(path: string): SettingsAccess | undefined {
  return SETTINGS_ACCESS[path]
}

/** Does this principal satisfy the rule? Shared by the hub and the router. */
export function canReachSettings(
  access: SettingsAccess,
  principal: { role?: string | undefined; permissions: ReadonlySet<string> },
): boolean {
  if (access.ownerOnly && principal.role !== 'OWNER') return false
  return principal.permissions.has(access.permission ?? SETTINGS_FLOOR_PERMISSION)
}
